import { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import Image from 'components/Image';
import { useScrollRestore } from 'hooks';
import ThemeProvider, { useTheme } from 'components/ThemeProvider';
import Footer from 'components/Footer';
import {
  ProjectContainer,
  ProjectSection,
  ProjectSectionContent,
  ProjectImage,
  ProjectTextRow,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectBackground,
  ProjectHeader,
  ProjectSectionColumns,
} from 'components/ProjectLayout';
import spBackground from 'assets/beyondDetail/pp-background.jpg';
import spBackgroundLarge from 'assets/beyondDetail/bd-background-large.jpg';
import spBackgroundPlaceholder from 'assets/beyondDetail/pp-background-placeholder.jpg';

import ppHome from 'assets/beyondDetail/bdHome.png';
import ppHomeLarge from 'assets/beyondDetail/bdHomeLarge.png';
import ppHomePlaceholder from 'assets/beyondDetail/bdHomePlaceholder.png';

import ppWorks from 'assets/beyondDetail/colorpal.jpg';
import ppWorksLarge from 'assets/beyondDetail/colorpal-large.jpg';
import ppWorksPlaceholder from 'assets/beyondDetail/colorpal-placeholder.jpg';

import bdSim from 'assets/beyondDetail/bd-sim.jpg';
import bdSimLarge from 'assets/beyondDetail/bd-sim-large.jpg';
import bdSimPlaceholder from 'assets/beyondDetail/bd-sim-placeholder.jpg';

import bdSBooking from 'assets/beyondDetail/bd-booking.jpg';
import bdSBookingLarge from 'assets/beyondDetail/bd-booking-large.jpg';
import bdSBookingPlaceholder from 'assets/beyondDetail/bd-booking-placeholder.jpg';

import ppRegister from 'assets/beyondDetail/pp-register2.jpg';
import ppRegisterLarge from 'assets/beyondDetail/pp-register-large2.jpg';
import ppRegisterPlaceholder from 'assets/beyondDetail/pp-register-placeholder2.jpg';

import spSlides from 'assets/beyondDetail/sp-slides.jpg';
import spSlidesLarge from 'assets/beyondDetail/sp-slides-large.jpg';
import spSlidesPlaceholder from 'assets/beyondDetail/sp-slides-placeholder.jpg';

import prerender from 'utils/prerender';
import { media } from 'utils/style';
import './index.css';

const title = 'Beyond Detail';
const description =
  'Design and development of a website for the auto detailing company Beyond Detail';
const technologies = 'ReactJS / Sanity';
const roles = ['Front-End Development', 'Sanity CMS'];

const BeyondDetail = () => {
  // const motionSectionRef = useRef();
  useScrollRestore();

  return (
    <Fragment>
      <Helmet>
        <title>{`Projects | ${title}`}</title>
        <meta name="description" content={description} />
      </Helmet>
      <ProjectContainer className="slice">
        <ProjectBackground
          srcSet={`${spBackground} 1280w, ${spBackgroundLarge} 2560w`}
          placeholder={spBackgroundPlaceholder}
          opacity={0.8}
          entered={!prerender}
        />
        <ProjectHeader
          title={title}
          description={description}
          technologies={technologies}
          linkLabel="Visit Website"
          url="https://www.beyonddetail.ca/"
          roles={roles}
        />
        <ProjectSection first>
          <ProjectSectionContent>
            <ProjectImage
              raised
              srcSet={`${ppHome} 1280w, ${ppHomeLarge} 2560w`}
              placeholder={ppHomePlaceholder}
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 800px, 1000px`}
              alt=""
            />
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectTextRow>
            <ProjectSectionHeading>The project</ProjectSectionHeading>
            <ProjectSectionText>
              I was contracted by the owners of an auto detailing company called Beyond
              Detail to create a custom website where it will be easy for their clients to
              view all the services that Beyond Detail provides as well as their works and
              pricing. The owner was looking for a sleek and professional website that
              matches the color theme of their brand.
            </ProjectSectionText>
          </ProjectTextRow>
        </ProjectSection>

        <ProjectSection light>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>Design System</ProjectSectionHeading>
              <ProjectSectionText>
                It was important to lay the foundations with a strong, flexible design
                system that could evolve during the product’s development cycle. This
                would inform both the aesthetics and user experience across the product
                itself as well as the website and marketing material.
              </ProjectSectionText>
            </ProjectTextRow>
            <Image
              srcSet={`${spSlides} 800w, ${spSlidesLarge} 1440w`}
              placeholder={spSlidesPlaceholder}
              alt=""
              sizes={`(max-width: ${media.mobile}px) 500px, (max-width: ${media.tablet}px) 800px, 1000px`}
            />
          </ProjectSectionContent>
        </ProjectSection>

        <ProjectSection>
          <ProjectSectionContent>
            <Image
              srcSet={`${bdSim} 800w, ${bdSimLarge} 1440w`}
              placeholder={bdSimPlaceholder}
              alt=""
              sizes={`(max-width: ${media.mobile}px) 500px, (max-width: ${media.tablet}px) 800px, 1000px`}
            />

            <ProjectTextRow>
              <ProjectSectionHeading>Window Tint Simulator</ProjectSectionHeading>
              <ProjectSectionText>
                For this project, I implemented a simulator where the clients could switch
                between the available window tints and see how they would appear on their
                cars.
              </ProjectSectionText>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>

        <ProjectSection light>
          <ProjectSectionColumns width="full">
            <Image
              raised
              srcSet={`${bdSBooking} 1280w, ${bdSBookingLarge} 2560w`}
              className="spr__video"
              src={bdSBookingLarge}
              placeholder={bdSBookingPlaceholder}
              alt="A learning designer building and deploying an interactive lesson on volcanism using the app."
              sizes={`(max-width: ${media.mobile}px) 100vw, 50vw`}
            />

            <ProjectSectionContent width="full">
              <ProjectTextRow width="s">
                <ProjectSectionHeading>Online Booking System</ProjectSectionHeading>
                <ProjectSectionText>
                  Having a way for clients to contact Beyond Detail about their services
                  was vital for the owner. I created a complete online booking system
                  where clients can specify the make of their car, services they are
                  interested in, and the date and time. I used Sanity to store the
                  information and used webhooks to automatically notify the owner with the
                  information whenever a new booking is created.
                </ProjectSectionText>
              </ProjectTextRow>
            </ProjectSectionContent>
          </ProjectSectionColumns>
        </ProjectSection>

        <ProjectSection>
          <ProjectSectionContent>
            <Image
              srcSet={`${ppWorks} 1280w, ${ppWorksLarge} 2560w`}
              placeholder={ppWorksPlaceholder}
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 800px, 1000px`}
              alt=""
            />
          </ProjectSectionContent>
        </ProjectSection>

        <ThemeProvider themeId="dark">
          <ProjectSection light>
            <ProjectSectionColumns width="full">
              <ProjectSectionContent width="full">
                <ProjectTextRow width="s">
                  <ProjectSectionHeading>Sanity</ProjectSectionHeading>
                  <ProjectSectionText>
                    I chose to integrate Sanity CMS in order to make it easy for the
                    owners to edit and add any information and pictures without needing to
                    go into the code. Sanity provided an external dashboard where the
                    owners can log in to the backend.
                  </ProjectSectionText>
                </ProjectTextRow>
              </ProjectSectionContent>
              <Image
                raised
                srcSet={`${ppRegister} 1280w, ${ppRegisterLarge} 2560w`}
                className="spr__video"
                src={ppRegisterLarge}
                placeholder={ppRegisterPlaceholder}
                alt="A learning designer building and deploying an interactive lesson on volcanism using the app."
                sizes={`(max-width: ${media.mobile}px) 100vw, 50vw`}
              />
            </ProjectSectionColumns>
          </ProjectSection>
        </ThemeProvider>
      </ProjectContainer>
      <Footer />
    </Fragment>
  );
};

export default BeyondDetail;
